"use client";

import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import React from "react";
import { z } from "zod";
import { ApiClientError, requestApi, requestApiJson } from "./api-client";
import {
  PublicAccountSchema,
  type PublicAccount,
} from "./auth-account-contracts";
import { clearDashboardUsage } from "./[locale]/dashboard/dashboard-personalization";

const SessionResponseSchema = z.discriminatedUnion("authenticated", [
  z
    .object({
      authenticated: z.literal(true),
      account: PublicAccountSchema,
    })
    .strict(),
  z
    .object({
      authenticated: z.literal(false),
      account: z.null(),
    })
    .strict(),
]);

type AuthState =
  | { readonly status: "loading"; readonly account: null }
  | { readonly status: "unavailable"; readonly account: null }
  | { readonly status: "anonymous"; readonly account: null }
  | { readonly status: "authenticated"; readonly account: PublicAccount };

type AuthContextValue = AuthState & {
  readonly logout: (redirectTo?: string) => Promise<void>;
  readonly retry: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false, refetchOnWindowFocus: false },
          mutations: { retry: false },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthStateProvider>{children}</AuthStateProvider>
    </QueryClientProvider>
  );
}

function AuthStateProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const [loggedOut, setLoggedOut] = useState(false);
  const { data, isError, isPending, refetch } = useQuery({
    queryKey: ["auth", "session"],
    queryFn: async (): Promise<PublicAccount | null> => {
      try {
        const session = SessionResponseSchema.parse(
          await requestApiJson("/api/v1/auth/session", z.unknown()),
        );
        return session.authenticated ? session.account : null;
      } catch (caught: unknown) {
        if (caught instanceof ApiClientError && caught.status === 401) {
          return null;
        }
        throw caught;
      }
    },
    networkMode: "always",
    staleTime: 30_000,
  });

  const retry = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const logout = useCallback(
    async (redirectTo?: string) => {
      try {
        await requestApi("/api/v1/auth/logout", {
          method: "POST",
        });
      } catch {
        // Local logout must still complete when the identity service is unavailable.
      }
      if (data !== null && data !== undefined) {
        clearDashboardUsage(window.localStorage, data.id);
      }
      queryClient.clear();
      setLoggedOut(true);
      if (redirectTo !== undefined) {
        window.location.replace(redirectTo);
        return;
      }
    },
    [data, queryClient],
  );

  const state = useMemo<AuthState>(
    () =>
      loggedOut
        ? { status: "anonymous", account: null }
        : isPending
          ? { status: "loading", account: null }
          : data === null
            ? { status: "anonymous", account: null }
            : data !== undefined
              ? { status: "authenticated", account: data }
              : isError
                ? { status: "unavailable", account: null }
                : { status: "loading", account: null },
    [data, isError, isPending, loggedOut],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, logout, retry }),
    [logout, retry, state],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return context;
}
